import React, { CSSProperties, useEffect, useState, useRef, useCallback, RefObject } from 'react';
import { createSpotClasses, Spot } from '../';
import { SpotPopoverContext } from './SpotPopoverContext';
import Close from './Close/Close';

import './SpotPopover.scss';

type Rect = {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
  x: number;
  y: number;
};

/**
 * Adjust the height of the viewport if we have a parent bounding box
 * @param boundingRect - Bounding box of the viewport as returned form getBoundingClientRect()
 * @param windowHeight - The current viewport height
 * @returns {*} - The new height of the popover
 */
SpotPopover.getAdjustedHeight = (boundingRect: Rect, windowHeight: number) => {
  if (!boundingRect) {
    return windowHeight;
  }

  const { bottom, top } = boundingRect;

  return bottom - top > windowHeight - top ? windowHeight - top : bottom - top;
};

/**
 * Adjust the width of the viewport if we have a parent bounding box.
 * @param boundingRect - Bounding box of the parent as returned from getBoundingClientRect()
 * @param windowWidth - The current width of the viewport
 * @returns {*} - The adjusted width of the popover
 */
SpotPopover.getAdjustedWidth = (boundingRect: Rect, windowWidth: number) => {
  if (!boundingRect) {
    return windowWidth;
  }

  const { right, left } = boundingRect;
  return right - left > windowWidth - left ? windowWidth - left : right - left;
};

/**
 * Adjust the popover bounding box so that it matches adjustments to the parent bounding box if supplied
 * @param popoverRect - Bounding rectangle of the popover
 * @param boundingRect - Bounding rectangle of the parent box. Can be null in which case the popover rect is just returned.
 * @returns {{top: number, left: number, bottom: number, width: *, x: *, y: *, right: number, height: *}|*}
 */
SpotPopover.getAdjustedRect = (popoverRect: Rect, boundingRect: Rect | null): Rect => {
  if (!boundingRect) {
    return popoverRect;
  }

  return {
    bottom: popoverRect.bottom - boundingRect.top,
    height: popoverRect.height,
    left: popoverRect.left - boundingRect.left,
    right: popoverRect.right - boundingRect.left,
    top: popoverRect.top - boundingRect.top,
    width: popoverRect.width,
    x: popoverRect.x,
    y: popoverRect.y,
  };
};

/**
 * Adjust height of popover if needed to fit on the screen.
 * @param inRect - Bounding rect of the popover as returned from getBoundingClientRect()
 * @param boundingRect - Bounding rect of the parent window as returned from getBoundingClientRect(). If null the viewport is used.
 * @param wrapperRect - Bounding rect of trigger component before popover shown.
 * @param currentWindowHeight - The current height of the viewport
 * @param buffer - How much of a buffer to have at the bottom or top of popover when resizing
 * @returns {number} - The height of the popover for the current parameters
 */
SpotPopover.getMaxPopoverHeightWithinBounds = (
  inRect: Rect,
  wrapperRect: Rect,
  boundingRect: Rect,
  currentWindowHeight: number,
  buffer = 1,
): number => {
  const windowHeight = SpotPopover.getAdjustedHeight(boundingRect, currentWindowHeight);
  const rect = SpotPopover.getAdjustedRect(inRect, boundingRect);
  // Height of caret on popover.
  const caretHeight = wrapperRect.height + 16 * 2;

  // Space available below popover to display
  const bottomHeight = rect.top + rect.height + caretHeight > windowHeight ? Math.abs(rect.top - windowHeight) - caretHeight : rect.height;
  //Space available above popover to display. Calculations when drawing on top are based on the popover displaying from the bottom.
  // We offset this by subtracting the height tof the trigger and caret height * 2.
  const topHeight =
    rect.top - rect.height - caretHeight > 0
      ? rect.height
      : windowHeight - Math.abs(rect.top - windowHeight) - caretHeight - wrapperRect.height;
  //The greater of the two is where we want to set the height.
  return buffer * (bottomHeight >= topHeight ? bottomHeight : topHeight);
};

/**
 * Test to see if the popover fits on the currently supplied viewport or parent bounding box.
 * @param inRect - Bounding box of the popover as returned from getBoundingClientRect()
 * @param boundingRect - Bounding box of the parent as returned from getBoundingClientRect()
 * @param currentWindowWidth - current width of the viewport
 * @param currentWindowHeight - current height of the viewport
 * @param percentVisible - The minimum amount as a % that each side must be visible. Default to 100.
 * @returns {boolean} - If the popover is at least the supplied percentage visible on each side.
 */

SpotPopover.isElementInViewport = (
  inRect: Rect,
  boundingRect: Rect,
  currentWindowWidth: number,
  currentWindowHeight: number,
  percentVisible = 100,
) => {
  const windowHeight = SpotPopover.getAdjustedHeight(boundingRect, currentWindowHeight);
  const windowWidth = SpotPopover.getAdjustedWidth(boundingRect, currentWindowWidth);
  const rect = SpotPopover.getAdjustedRect(inRect, boundingRect);

  const { top, left, bottom, right, width, height } = rect;

  // Will popover fit in the screen height
  const fitHeight = 1 - Math.min(0, top / height) < percentVisible / 100;
  // Will popover fit in screen width;
  const fitWidth = Math.floor(100 - ((left >= 0 ? 0 : left) / +-(width / 1)) * 100) < percentVisible;
  // Is on screen vertically
  const onScreenVertical = Math.floor(100 - ((bottom - windowHeight) / height) * 100) < percentVisible;
  // Is on screen horizontal
  const onScreenHorizontal = Math.floor(100 - ((right - windowWidth) / width) * 100) < percentVisible;

  return !(fitHeight || fitWidth || onScreenVertical || onScreenHorizontal);
};

SpotPopover.getDrawClasses = (defaultDrawLocation: string) => {
  const drawClasses = [
    'spot-popover--bottom-center',
    'spot-popover--top-center',
    'spot-popover--bottom-left',
    'spot-popover--bottom-right ',
    'spot-popover--bottom-far-left',
    'spot-popover--bottom-far-right',
    'spot-popover--top-left',
    'spot-popover--top-far-left',
    'spot-popover--top-right',
    'spot-popover--top-far-right',
  ];

  if (defaultDrawLocation === 'top') {
    const temp = drawClasses[0];
    drawClasses[0] = drawClasses[1];
    drawClasses[1] = temp;
  } else if (defaultDrawLocation === 'left') {
    const temp = drawClasses[0];
    drawClasses[0] = drawClasses[2];
    drawClasses.splice(2, 1, temp);
  } else if (defaultDrawLocation === 'right') {
    const temp = drawClasses[0];
    drawClasses[0] = drawClasses[3];
    drawClasses.splice(3, 1, temp);
  }

  return drawClasses;
};

// Only three widths available for this component.
SpotPopover.widths = { small: '130px', medium: '175px', large: '240px', xlarge: '360px', xxlarge: '460px' };

interface Props {
  TriggerComponent?: any;
  children?: any;
  disabled?: any;
  parentRef?: RefObject<HTMLHeadingElement | null>;
  // Additional classes to pass in for styling the popover.
  popoverClassName?: string;
  // Additional style for the popover
  popoverWrapperClassName?: string;
  popoverWrapperStyles?: Record<string, unknown>;
  width?: string;
  defaultDrawLocation?: string;
  isFullScreen?: boolean;
}

//TestIds are useful for automated tests. We declare them as static values for ease of reference.
SpotPopover.testIds = {
  popoverContainer: 'popover-container',
  popoverStyleContainer: 'popover-style',
  popoverMainComponent: 'popover-main',
};

/**
 * Render the popover.
 * @param children - The elements for the popover
 * @param TriggerComponent - The component to trigger the popover when clicked
 * @param parentRef - A reference to the parent that the popover will be located in. Used for the bounding box, can be null if viewport is used
 * @param popoverClassName - custom classes to pass to the popover
 * @param popoverWrapperClassName - custom classes to pass to the popoverComponent Wrapper
 * @param popoverWrapperStyles - Style to pass to the popover component classes
 * @param width - The width of the popover (small, medium, large, xlarge)
 * @param disabled
 * @returns {*}
 * @constructor
 */
function SpotPopover({
  children,
  TriggerComponent,
  parentRef,
  popoverClassName = '',
  popoverWrapperClassName = '',
  // Set default height and enable scrolling. This can be overridden by passing in custom styles.
  popoverWrapperStyles = {},
  width = 'medium',
  disabled,
  defaultDrawLocation = 'bottom',
  isFullScreen = false,
}: Props) {
  const defaultPopoverClasses = `spot-popover-hide ${createSpotClasses(Spot.block.popover, undefined, [Spot.modifier.bottomCenter])}`;
  const [popoverClasses, setPopoverClasses] = useState(defaultPopoverClasses);
  // Only the full screen popover is scrollable. A regular popover sizes to its
  const [popoverStyles, setPopoverStyles] = useState<CSSProperties>({});
  const boundingRect = useRef(null);
  const popoverWrapperRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLInputElement>(null);

  const popoverEdgeDetectionClasses = SpotPopover.getDrawClasses(defaultDrawLocation);

  const isPopoverVisible = useCallback(() => {
    return !popoverRef?.current?.classList?.contains('spot-popover-hide');
  }, []);

  const hidePopover = useCallback(() => {
    setPopoverClasses(defaultPopoverClasses);
  }, [defaultPopoverClasses]);

  const findVisibleClass = useCallback(() => {
    const shownClass = createSpotClasses(Spot.block.popover, undefined, [Spot.modifier.shown]);

    if (isFullScreen) {
      return `spot-popover--full-screen ${shownClass}`;
    }

    const popover = popoverRef.current;
    if (!popover) {
      return `spot-popover-hide ${shownClass}`;
    }

    const height = window.innerHeight || document.documentElement.clientHeight;
    const width = window.innerWidth || document.documentElement.clientWidth;
    const renderedClassName = popover.className;

    try {
      // Loop through the classes, if we find one that fits the current screen, return it.
      for (const edgeClass of popoverEdgeDetectionClasses) {
        popover.className = `spot-popover-hide ${edgeClass} ${shownClass} ${popoverClassName}`;
        if (SpotPopover.isElementInViewport(popover.getBoundingClientRect(), boundingRect.current!, width, height)) {
          return `${edgeClass} ${shownClass}`;
        }
      }
    } finally {
      // Hand the class name back to React so the next render is not fighting this.
      popover.className = renderedClassName;
    }

    return `spot-popover-hide ${shownClass}`;
  }, [boundingRect, popoverRef, popoverEdgeDetectionClasses, isFullScreen, popoverClassName]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node;
      if (!popoverWrapperRef?.current?.contains(target)) {
        hidePopover();
      } else if (!popoverRef.current!.contains(target) && !disabled) {
        // Hide the popover and reset to default before checking on the location
        if (isPopoverVisible()) {
          hidePopover();
        } else {
          // Find the class that fits the screen best.
          const visibleClass = findVisibleClass();
          const popover = popoverRef.current!;
          const height = window.innerHeight || document.documentElement.clientHeight;

          const renderedClassName = popover.className;
          popover.className = `spot-popover-hide ${visibleClass} ${popoverClassName}`;

          const availableHeight = SpotPopover.getMaxPopoverHeightWithinBounds(
            popover.getBoundingClientRect(),
            popoverWrapperRef.current!.getBoundingClientRect(),
            boundingRect.current!,
            height,
            1,
          );
          const contentHeight = popover.scrollHeight;
          popover.className = renderedClassName;

          // Constrain and scroll only when the content genuinely does not fit.
          setPopoverStyles(
            isFullScreen
              ? { overflow: 'auto', maxHeight: '100%' }
              : contentHeight > availableHeight
                ? { overflow: 'auto', maxHeight: availableHeight }
                : {},
          );

          setPopoverClasses(visibleClass);
        }
      }
      return `spot-popover-hide ${createSpotClasses(Spot.block.popover, undefined, [Spot.modifier.shown])}`;
    },
    [boundingRect, popoverRef, popoverWrapperRef, disabled, findVisibleClass, hidePopover, isFullScreen],
  );

  const handleResize = useCallback(() => {
    // @ts-ignore
    boundingRect.current = parentRef && parentRef.current ? parentRef.current.getBoundingClientRect() : null;
  }, [parentRef]);

  const handleResizePopover = useCallback(() => {
    if (isPopoverVisible() && isFullScreen) {
      hidePopover();
    }
  }, [isPopoverVisible, isFullScreen, hidePopover]);

  useEffect(() => {
    // @ts-ignore
    boundingRect.current = parentRef && parentRef.current && parentRef.current.getBoundingClientRect();

    document.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResizePopover);

    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResizePopover);
    };
  }, [handleClick, handleResize, handleResizePopover, parentRef]);

  return (
    <span
      className={`spot-popover-wrapper ${popoverWrapperClassName}`}
      style={popoverWrapperStyles}
      data-testid={SpotPopover.testIds.popoverMainComponent}
      ref={popoverWrapperRef}
    >
      {TriggerComponent}
      <div
        className={`${popoverClasses} ${popoverClassName}`}
        data-testid={SpotPopover.testIds.popoverContainer}
        ref={popoverRef}
        style={!isFullScreen ? { width: SpotPopover.widths[width as keyof typeof SpotPopover.widths], borderRadius: 0 } : {}}
      >
        {isFullScreen && (
          <div className={'popover-close-box'} onClick={() => hidePopover()}>
            <Close />
          </div>
        )}

        <div style={popoverStyles} data-testid={SpotPopover.testIds.popoverStyleContainer}>
          <SpotPopoverContext.Provider value={{ hidePopover, popoverVisible: isPopoverVisible }}>
            {isFullScreen && <div className={'full-screen-children'}>{children}</div>}
            {!isFullScreen && children}
          </SpotPopoverContext.Provider>
        </div>
      </div>
    </span>
  );
}

export default SpotPopover;
